import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { createUniversity, updateUniversity, fetchUniversityById, clearSelectedUniversity } from '../../store/slices/universitySlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Card, Button, Input } from '../../components/Common';

const UniversityForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { selectedUniversity } = useSelector((state) => state.university);

  const isEdit = Boolean(id);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', description: '', established_on: '', students_capacity: '',
    numbers_of_faculty: '', country: '', state: '', city: '', zip_code: ''
  });

  useEffect(() => {
    if (isEdit) {
      dispatch(fetchUniversityById({ id, includeCourses: false }));
    }
    return () => { dispatch(clearSelectedUniversity()); };
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && selectedUniversity) {
      setFormData({
        name: selectedUniversity.name || '',
        description: selectedUniversity.description || '',
        established_on: selectedUniversity.established_on || '',
        students_capacity: selectedUniversity.students_capacity || '',
        numbers_of_faculty: selectedUniversity.numbers_of_faculty || '',
        country: selectedUniversity.country || '',
        state: selectedUniversity.state || '',
        city: selectedUniversity.city || '',
        zip_code: selectedUniversity.zip_code || ''
      });
    }
  }, [isEdit, selectedUniversity]);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.description.trim()) {
      dispatch(showError('Name and description are required'));
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        students_capacity: formData.students_capacity ? parseInt(formData.students_capacity) : undefined,
        numbers_of_faculty: formData.numbers_of_faculty ? parseInt(formData.numbers_of_faculty) : undefined
      };

      if (isEdit) {
        await dispatch(updateUniversity({ id, data: submitData })).unwrap();
        dispatch(showSuccess('University updated successfully'));
      } else {
        await dispatch(createUniversity(submitData)).unwrap();
        dispatch(showSuccess('University created successfully'));
      }
      navigate('/universities');
    } catch (error) {
      dispatch(showError(error || `Failed to ${isEdit ? 'update' : 'create'} university`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/universities')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
          <ArrowLeftIcon className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{isEdit ? 'Edit University' : 'New University'}</h1>
          <p className="text-slate-500">{isEdit ? 'Update university information' : 'Add a new university'}</p>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input label="Name *" value={formData.name} onChange={handleChange('name')} placeholder="University name" required />
          <Input label="Description *" value={formData.description} onChange={handleChange('description')} placeholder="Description" required />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Input label="Established On" type="date" value={formData.established_on} onChange={handleChange('established_on')} />
            <Input label="Student Capacity" type="number" value={formData.students_capacity} onChange={handleChange('students_capacity')} placeholder="e.g., 10000" />
            <Input label="Number of Faculty" type="number" value={formData.numbers_of_faculty} onChange={handleChange('numbers_of_faculty')} placeholder="e.g., 500" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Input label="Country" value={formData.country} onChange={handleChange('country')} placeholder="Country" />
            <Input label="State" value={formData.state} onChange={handleChange('state')} placeholder="State/Province" />
            <Input label="City" value={formData.city} onChange={handleChange('city')} placeholder="City" />
            <Input label="Zip Code" value={formData.zip_code} onChange={handleChange('zip_code')} placeholder="Zip/Postal code" />
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
            <Button type="submit" loading={submitting}>{isEdit ? 'Update University' : 'Create University'}</Button>
            <Button variant="secondary" onClick={() => navigate('/universities')}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default UniversityForm;